import React from "react";
// @tanstack/react-query, not the v3 `react-query` package this originally imported. Both are
// in package.json, so the build never complained — but App.js mounts only the v4 provider, so
// the v3 hook threw "No QueryClient set" the moment this tab rendered.
import { useQuery } from "@tanstack/react-query";
import { Box, Divider, LinearProgress, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import RepeatIcon from "@mui/icons-material/Repeat";
import { useIntl } from "react-intl";
import { fetchAssetConsumption, fetchScheduleConsumption, fetchSchedules } from "../../../fetchers/cmmsAssetConsumption";
import { formatDurationHours, formatQuantity } from "../cmmsFormat";
import {
  cmmsTypeScale,
  getAssetHierarchyPalette,
  getSectionBoxSx,
  getStatLabelSx,
  getStatValueSx,
  getStatisticTileSx,
} from "../assetHierarchyStyles";
import { CmmsEmptyState, CmmsErrorState, CmmsListSkeleton, formatCmmsError } from "./CmmsStates";
import ScheduleProgress from "./ScheduleProgress";
import messages from "../messages";

/*
 * Label above value, like every other statistic tile in the module.
 *
 * This one used to put the value first and drop the label's uppercase and letterSpacing, which
 * made a tile sitting inside the same asset panel as the header's tiles read as a different kind
 * of thing. FE-15.7 turned it the right way up — the scale lives in assetHierarchyStyles now, and
 * the point of that is that a tile cannot quietly invent its own.
 */
function StatTile({ label, value, subtitle, cmmsColors }) {
  const sx = getStatisticTileSx(cmmsColors);

  return (
    <Box sx={sx}>
      <Typography variant={cmmsTypeScale.statLabel} sx={getStatLabelSx(cmmsColors)}>
        {label}
      </Typography>
      <Typography variant={cmmsTypeScale.statValue} sx={getStatValueSx(cmmsColors)}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" sx={{ color: cmmsColors.subtleText, display: "block" }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

function ScheduleRow({ schedule, companyKey, cmmsColors }) {
  const intl = useIntl();
  const isRunningTime = schedule.triggerBasis === 2;

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["cmmsScheduleConsumption", companyKey, schedule.id],
    queryFn: () => fetchScheduleConsumption(companyKey, schedule.id),
    staleTime: 60_000,
    enabled: Boolean(companyKey && schedule.id),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 1 }}>
        <Box sx={{ flex: 1 }}>
          <LinearProgress variant="indeterminate" sx={{ height: 8, borderRadius: 1 }} />
        </Box>
      </Box>
    );
  }

  if (isError) {
    const message = formatCmmsError(error, intl.formatMessage({ defaultMessage: "Failed to load" }));
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.75 }}>
        <Typography variant="body2" sx={{ color: cmmsColors.text, fontWeight: 700, minWidth: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {schedule.title}
        </Typography>
        <Typography variant="body2" sx={{ color: cmmsColors.dangerText, flexShrink: 0 }}>
          {message}
        </Typography>
        <Typography
          variant="body2"
          component="button"
          onClick={() => refetch()}
          sx={{ color: cmmsColors.accent, fontWeight: 700, cursor: "pointer", background: "none", border: "none", p: 0, flexShrink: 0 }}
        >
          {intl.formatMessage({ defaultMessage: "Retry" })}
        </Typography>
      </Box>
    );
  }

  const iconColor = cmmsColors.subtleText;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 0.75 }}>
      <Typography variant="body2" sx={{ color: cmmsColors.text, fontWeight: 700, minWidth: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {schedule.title}
      </Typography>

      <Tooltip title={intl.formatMessage(isRunningTime ? messages.usageScheduleRunningTime : messages.usageScheduleQuantity)} arrow>
        <Box sx={{ color: iconColor, display: "flex", alignItems: "center", flexShrink: 0 }}>
          {isRunningTime ? <ScheduleIcon fontSize="small" /> : <RepeatIcon fontSize="small" />}
        </Box>
      </Tooltip>

      <ScheduleProgress reading={data} warnAtPercent={schedule.warnAtPercent} cmmsColors={cmmsColors} />
    </Box>
  );
}

export default function AssetUsageTab({ companyKey, assetNodeId }) {
  const theme = useTheme();
  const intl = useIntl();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);

  const consumptionQuery = useQuery({
    queryKey: ["cmmsAssetConsumption", companyKey, assetNodeId],
    queryFn: () => fetchAssetConsumption(companyKey, assetNodeId),
    staleTime: 60_000,
    enabled: Boolean(companyKey && assetNodeId),
  });

  const schedulesQuery = useQuery({
    queryKey: ["cmmsRecurringSchedules", companyKey, { assetNodeId }],
    queryFn: () => fetchSchedules(companyKey, assetNodeId),
    staleTime: 60_000,
    enabled: Boolean(companyKey && assetNodeId),
  });

  if (consumptionQuery.isLoading) {
    return (
      <Box sx={getSectionBoxSx(cmmsColors)}>
        <CmmsListSkeleton rows={3} />
      </Box>
    );
  }

  if (consumptionQuery.isError) {
    return (
      <CmmsErrorState
        error={consumptionQuery.error}
        fallback={intl.formatMessage({ defaultMessage: "Could not load consumption data" })}
        onRetry={consumptionQuery.refetch}
      />
    );
  }

  const consumption = consumptionQuery.data;
  const hasNoMachines = !consumption || consumption.machineCount === 0;

  const sectionSx = getSectionBoxSx(cmmsColors);

  if (hasNoMachines) {
    return (
      <CmmsEmptyState
        title={intl.formatMessage(messages.usageNoMachines)}
        hint={intl.formatMessage(messages.usageNoMachinesHint)}
      />
    );
  }

  const nonCalendarSchedules = (schedulesQuery.data ?? []).filter((s) => s.triggerBasis !== 1);

  return (
    <Stack spacing={1.5}>
      <Box sx={sectionSx}>
        <Stack direction="row" spacing={1.5}>
          <StatTile
            label={intl.formatMessage(messages.usageRunTime)}
            value={formatDurationHours(intl, consumption.runningTimeHours)}
            subtitle={intl.formatMessage(messages.usageMachineCount, { count: consumption.machineCount })}
            cmmsColors={cmmsColors}
          />
          <StatTile
            label={intl.formatMessage(messages.usageCycles)}
            value={formatQuantity(intl, consumption.quantity, 0)}
            cmmsColors={cmmsColors}
          />
        </Stack>
      </Box>

      {schedulesQuery.isLoading && (
        <Box sx={sectionSx}>
          <CmmsListSkeleton rows={3} />
        </Box>
      )}

      {schedulesQuery.isError && (
        <Box sx={sectionSx}>
          <CmmsErrorState
            error={schedulesQuery.error}
            fallback={intl.formatMessage({ defaultMessage: "Could not load schedules" })}
            onRetry={schedulesQuery.refetch}
          />
        </Box>
      )}

      {schedulesQuery.isSuccess && nonCalendarSchedules.length > 0 && (
        <Box sx={sectionSx}>
          <Stack divider={<Divider sx={{ borderColor: cmmsColors.border }} />} spacing={0.5}>
            {nonCalendarSchedules.map((schedule) => (
              <ScheduleRow key={schedule.id} schedule={schedule} companyKey={companyKey} cmmsColors={cmmsColors} />
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}